import { url as baseUrl } from "api";

// Typed client for the Optimize backend contract (see API_REQUIREMENTS.md and
// Lukas's Message_to_claude_code.md). The generic helpers in src/api.ts swallow
// status codes and can't send raw bodies, so this module talks to fetch directly:
// 409 rule refusals carry a machine-readable body we need, and creative uploads
// are raw video bytes, not JSON.

export type Platform = "android" | "ios";

// persisted backend keys — 12 cards, fixed
export type OptimizationKey =
  | "recurring_event_structure"
  | "rewarded_ad_events"
  | "override_creatives"
  | "retention_challenges"
  | "live_ops_collectables_playtime"
  | "time_limited_challenges"
  | "daily_playtime_challenges"
  | "daily_rewarded_ad_challenges"
  | "purchase_challenges"
  | "burning_rewards"
  | "multi_game_purchase_challenges"
  | "live_ops_collectables_events";

export type CardStatus = "enabled" | "paused" | "disabled";

export type RefusalCode =
  | "max_active_challenges_reached"
  | "activation_cooldown"
  | "iap_share_too_low"
  // no IAP share on record at all — distinct from one that is too low, and
  // nothing the partner can fix by monetising differently (CONTRACT.md)
  | "iap_share_unknown"
  | "event_name_required"
  | "params_required"
  | "max_slots_reached";

export interface ActivationRefusal {
  code: RefusalCode;
  activeCount?: number;
  maxActive?: number;
  lastActivatedAt?: string;
  nextAvailableAt?: string;
  // null for titles with no revenue split on record
  iapSharePercent?: number | null;
  requiredIapSharePercent?: number | null;
  maxSlots?: number;
}

// event already on file for a card, per app — the "already on file" signal also
// arrives here (not just on the board), keyed by optimization key
export interface AppEventPrefill {
  eventName: string;
  eventNameSource: string;
}

export interface OptimizeApp {
  appId: string; // store identifier string (Play package name / App Store id)
  packageName?: string;
  platform: Platform;
  // null for titles with no store metadata on record yet
  appName: string | null;
  iconUrl: string | null;
  // store-listing creatives — one banner + one video per game, no language
  // dimension; the fallback when a language has no partner override
  bannerImageUrl?: string | null;
  // ⚠️ not guaranteed playable in <video>: titles video_downloader hasn't
  // processed still carry the original YouTube embed URL — check with
  // isYouTubeEmbedUrl and render those in an iframe
  videoUrl?: string | null;
  iapSharePercent: number | null;
  eligibility: { purchaseChallenges: boolean };
  events?: Partial<Record<OptimizationKey, AppEventPrefill>>;
  activeChallengeCount: number;
  lastActivatedAt: string | null;
  nextActivationAvailableAt: string | null;
}

export interface OptimizeStateEntry {
  optimization: OptimizationKey;
  appId: string;
  platform: Platform;
  slot: number;
  status: CardStatus;
  // the LATEST activation (resuming moves it forward), per CONTRACT.md
  activatedAt: string | null;
}

export interface TimeLimitedParams {
  eventName: string;
  eventType: "recurring" | "unique";
  eventCount?: number; // required for recurring, rejected for unique
  taskName: string;
  timeLimitDays: number;
}

export interface BoardEntry {
  slot: number;
  status: CardStatus;
  isEnabled: boolean;
  eventName: string | null;
  // "stored" when typed here; source table name when prefilled — the
  // "already on file" signal (partner_app_setup_repeatable / app_ad_events /
  // partner_app_configurations)
  eventNameSource: string;
  params?: TimeLimitedParams;
  activatedAt: string | null;
  // present on an OFF slot: what a PUT enabling it would return
  activationRefusal?: ActivationRefusal;
  // present on an ON time-limited slot whose params can't currently be
  // rewritten (cooldown-gated edit); mutually exclusive with activationRefusal
  editRefusal?: ActivationRefusal;
}

export interface BoardCard {
  key: OptimizationKey;
  group: "event_structure" | "bonus_challenge";
  maxSlots: number;
  requiresEventName: boolean;
  requiresParams: boolean;
  countsTowardActiveLimit: boolean;
  entries: BoardEntry[];
}

export interface OptimizeBoard {
  rules: { maxActiveChallengesPerApp: number; activationCooldownHours: number };
  app: OptimizeApp | null;
  cards: BoardCard[];
}

export interface OptimizeCreative {
  id: number;
  languageCode: string;
  // banner or video — the client renders the video when a language has one and
  // falls back to the banner, so it can't infer the kind from contentType alone
  type: "banner" | "video";
  fileName: string;
  fileSize: number;
  contentType: string;
  url: string; // short-lived presigned URL
  updatedAt: string | null;
}

export interface PutOptimizationBody {
  appId: string;
  platform: Platform;
  // single-slot cards: omit. time_limited_challenges: omit when adding
  // (takes the first free slot), required when pausing/editing an entry.
  slot?: number;
  isEnabled?: boolean;
  // omitted fields keep their stored values; send "" / null to clear
  eventName?: string | null;
  params?: TimeLimitedParams | null;
}

// Two error shapes are in play: rule refusals carry a machine-readable
// lowercase `code`, while the service's generic errors are
// {"err": "…: bad request", "success": false}. Keep both — the `err` text is
// the only thing we can show the partner when there's no code.
export class OptimizeApiError extends Error {
  status: number;
  code?: RefusalCode | string;
  refusal?: ActivationRefusal;
  serverMessage?: string;

  constructor(status: number, body: any) {
    const err = typeof body?.err === "string" ? cleanServerError(body.err) : undefined;
    super(body?.code ?? err ?? `HTTP ${status}`);
    this.status = status;
    this.code = body?.code;
    this.refusal = body?.code ? (body as ActivationRefusal) : undefined;
    this.serverMessage = err;
  }
}

// go errors arrive wrapped, e.g. "invalid language code: bad request" — drop the
// trailing classification and capitalise what's left
const cleanServerError = (err: string): string => {
  const text = err
    .split(":")
    .filter((part) => !/^\s*(bad request|not found|conflict|internal( server)? error)\s*$/i.test(part))
    .join(":")
    .trim();
  if (!text) return err;
  const sentence = text.charAt(0).toUpperCase() + text.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
};

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("jwtToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const appQuery = (appId: string, platform: Platform) =>
  `appId=${encodeURIComponent(appId)}&platform=${platform}`;

const request = async (
  method: string,
  path: string,
  body?: object
): Promise<any> => {
  const response = await fetch(baseUrl + path, {
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...authHeaders(),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) throw new OptimizeApiError(response.status, json);
  return json;
};

// ------------------------------------------------------------------ endpoints

export const fetchApps = async (): Promise<OptimizeApp[]> => {
  // the service answers with {"apps": [...]}; Lukas's doc described a bare
  // array, so accept both rather than break on whichever ships
  const response = await request("GET", "/dashboard/optimize/apps");
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.apps)) return response.apps;
  throw new Error("unexpected /apps shape");
};

export const fetchState = async (): Promise<OptimizeStateEntry[]> => {
  const response = await request("GET", "/dashboard/optimize/state");
  return response?.states ?? [];
};

// per-game board; omit both params for the card catalogue (app: null)
export const fetchBoard = (
  appId?: string,
  platform?: Platform
): Promise<OptimizeBoard> =>
  request(
    "GET",
    appId && platform
      ? `/dashboard/optimize?${appQuery(appId, platform)}`
      : "/dashboard/optimize"
  );

// returns the game's whole board (one activation changes other cards' refusals)
export const putOptimization = (
  key: OptimizationKey,
  body: PutOptimizationBody
): Promise<OptimizeBoard> => request("PUT", `/dashboard/optimize/${key}`, body);

// ------------------------------------------------------------------ creatives

export const fetchCreatives = async (
  appId: string,
  platform: Platform
): Promise<OptimizeCreative[]> => {
  const response = await request(
    "GET",
    `/dashboard/optimize/creatives?${appQuery(appId, platform)}`
  );
  // a bare array per CREATIVES_FRONTEND_GUIDE.md (empty = nothing uploaded yet,
  // the normal starting state) — tolerate an {creatives: [...]} envelope the
  // same way fetchApps does
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.creatives)) return response.creatives;
  throw new Error("unexpected /creatives shape");
};

// 200 + the created Creative; replaces only this language *and* kind
export const uploadCreative = async (
  languageCode: string,
  appId: string,
  platform: Platform,
  file: File
): Promise<OptimizeCreative> => {
  const response = await fetch(
    `${baseUrl}/dashboard/optimize/creatives/${languageCode}?${appQuery(
      appId,
      platform
    )}`,
    {
      method: "PUT",
      headers: {
        Accept: "application/json",
        // raw body, not multipart; extension must agree with the content type
        "Content-Type": file.type,
        "Content-Disposition": `attachment;filename=${file.name}`,
        ...authHeaders(),
      },
      body: file,
    }
  );
  const json = await response.json().catch(() => null);
  if (!response.ok) throw new OptimizeApiError(response.status, json);
  return json;
};

export const deleteCreative = (creativeId: number): Promise<any> =>
  request("DELETE", `/dashboard/optimize/creatives/${creativeId}`);

// ----------------------------------------------- client-side creative checks

// The backend only enforces content type and the per-kind size caps (no ffmpeg
// in the service image) — dimension/duration specs are validated here, and only
// here (CONTRACT.md "Not implemented" §2).
export interface CreativeValidationError {
  message: string;
}

// the backend's own caps (CONTRACT.md): request-body bounds, not specs — a
// normal promo video or store header is nowhere near them
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export const CREATIVE_VIDEO_TYPES = ["video/mp4", "video/quicktime"];
export const CREATIVE_IMAGE_TYPES = ["image/jpeg", "image/png"];

// the backend 400s when the extension disagrees with the content type — a
// mismatch usually means a mislabelled upload, so catch it before uploading
const EXTENSIONS_BY_TYPE: Record<string, string[]> = {
  "video/mp4": ["mp4"],
  "video/quicktime": ["mov", "qt"],
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
};

// store trailers video_downloader hasn't re-hosted yet arrive as the original
// YouTube embed URL — showable in an iframe, not in a <video> tag
export const isYouTubeEmbedUrl = (url: string): boolean =>
  /(^|\.)youtube\.com\/embed\//.test(url);

const probeVideo = (file: File) =>
  new Promise<{ width: number; height: number; duration: number } | null>(
    (resolve) => {
      const video = document.createElement("video");
      const objectUrl = URL.createObjectURL(file);
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration,
        });
      };
      video.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      video.src = objectUrl;
    }
  );

const probeImage = (file: File) =>
  new Promise<{ width: number; height: number } | null>((resolve) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    image.src = objectUrl;
  });

export const validateCreativeFile = async (
  file: File
): Promise<CreativeValidationError | null> => {
  const isVideo = CREATIVE_VIDEO_TYPES.includes(file.type);
  const isImage = CREATIVE_IMAGE_TYPES.includes(file.type);
  if (!isVideo && !isImage)
    return { message: "Format must be MP4/MOV (video) or JPG/PNG (image)." };

  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const allowed = EXTENSIONS_BY_TYPE[file.type];
  if (allowed && !allowed.includes(extension))
    return {
      message: `File extension ".${extension}" doesn't match its ${file.type} content — expected ${allowed
        .map((e) => `.${e}`)
        .join(" / ")}.`,
    };

  if (isVideo && file.size > MAX_VIDEO_BYTES)
    return { message: "Max video size is 100 MB." };
  if (isImage && file.size > MAX_IMAGE_BYTES)
    return { message: "Max image size is 10 MB." };

  const probe = isVideo ? await probeVideo(file) : await probeImage(file);

  // browser can't decode it (e.g. some MOV codecs) — let the upload proceed
  if (!probe) return null;

  if (probe.width < 1280 || probe.height < 720)
    return { message: "Minimum resolution is 1280×720." };
  if (probe.width <= probe.height)
    return { message: "Orientation must be horizontal (landscape)." };
  if (Math.abs(probe.width / probe.height - 16 / 9) > 0.02)
    return { message: "Aspect ratio must be 16:9." };
  // 15–30 s stays the recommendation in the specs copy; the hard gate is
  // deliberately looser (2–60 s) per Günay so decent creatives aren't rejected
  if ("duration" in probe && (probe.duration < 2 || probe.duration > 60))
    return { message: "Video length must be between 2 and 60 seconds." };
  return null;
};

// languageCode ∈ en de fr es it pt nl pl ja ko — Turkish dropped, Dutch /
// Italian / Polish added per Günay; the backend's accepted set must match or
// uploads for the new codes 400 with "invalid language code"
export const CREATIVE_LANGUAGE_CODES: { code: string; label: string }[] = [
  { code: "en", label: "English" },
  { code: "de", label: "German" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "nl", label: "Dutch" },
  { code: "pl", label: "Polish" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
];

// list order mirrors render priority: language order above, video before banner
// within a language (the video is what plays when both exist)
export const sortCreatives = (
  creatives: OptimizeCreative[]
): OptimizeCreative[] => {
  const languageRank = (code: string) => {
    const index = CREATIVE_LANGUAGE_CODES.findIndex((l) => l.code === code);
    return index === -1 ? CREATIVE_LANGUAGE_CODES.length : index;
  };
  return [...creatives].sort(
    (a, b) =>
      languageRank(a.languageCode) - languageRank(b.languageCode) ||
      (a.type === b.type ? 0 : a.type === "video" ? -1 : 1)
  );
};
